import { baseApi } from '@/services/api';
import { CollaborationDocument, CollaborationSession } from './collaborationTypes';

export const collaborationAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Documents
    createDocument: builder.mutation<CollaborationDocument, {
      name: string;
      content: string;
      document_type: string;
      project_id?: string;
      is_public?: boolean;
    }>({
      query: (data) => ({
        url: '/collaboration/documents',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['CollaborationDocuments'],
    }),
    
    getDocuments: builder.query<CollaborationDocument[], { project_id?: string; limit?: number }>({
      query: (params) => ({
        url: '/collaboration/documents',
        method: 'GET',
        params,
      }),
      providesTags: ['CollaborationDocuments'],
    }),
    
    getDocument: builder.query<CollaborationDocument, string>({
      query: (id) => ({
        url: `/collaboration/documents/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'CollaborationDocuments', id }],
    }),
    
    updateDocument: builder.mutation<void, { id: string; data: Partial<CollaborationDocument> }>({
      query: ({ id, data }) => ({
        url: `/collaboration/documents/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'CollaborationDocuments', id }],
    }),
    
    inviteCollaborator: builder.mutation<void, { document_id: string; email: string; permission: string }>({
      query: (data) => ({
        url: `/collaboration/documents/${data.document_id}/invite`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['CollaborationDocuments'],
    }),
    
    removeCollaborator: builder.mutation<void, { document_id: string; user_id: string }>({
      query: ({ document_id, user_id }) => ({
        url: `/collaboration/documents/${document_id}/collaborators/${user_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CollaborationDocuments'],
    }),
    
    getDocumentHistory: builder.query<any, { document_id: string; limit?: number }>({
      query: ({ document_id, limit = 50 }) => ({
        url: `/collaboration/documents/${document_id}/history`,
        method: 'GET',
        params: { limit },
      }),
    }),
    
    getActiveSessions: builder.query<CollaborationSession[], void>({
      query: () => ({
        url: '/collaboration/sessions',
        method: 'GET',
      }),
      providesTags: ['CollaborationSessions'],
    }),
    
    getCollaborationStatus: builder.query<any, void>({
      query: () => ({
        url: '/collaboration/status',
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useCreateDocumentMutation,
  useGetDocumentsQuery,
  useLazyGetDocumentsQuery,
  useGetDocumentQuery,
  useLazyGetDocumentQuery,
  useUpdateDocumentMutation,
  useInviteCollaboratorMutation,
  useRemoveCollaboratorMutation,
  useGetDocumentHistoryQuery,
  useLazyGetDocumentHistoryQuery,
  useGetActiveSessionsQuery,
  useGetCollaborationStatusQuery,
} = collaborationAPI;
